import { useEffect, useState } from 'react';
import { riskAPI, type RiskAssessment, type RiskCategory, type RiskLevel } from '../../lib/api/risk';
import { getErrorMessage } from '../../hooks/useFetchData';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface RiskAssessmentDialogProps {
  open: boolean;
  onClose: () => void;
  supplierId: number;
  customerId: number;
  /** The logged-in customer user, recorded as the assessor. */
  assessorId: number;
  categories: RiskCategory[];
  onSaved: (assessment: RiskAssessment) => void;
}

interface CellState { likelihood: number; consequence: number; notes: string }

const LIKELIHOOD = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost certain'];
const CONSEQUENCE = ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic'];

function cellLevel(l: number, c: number): RiskLevel {
  const v = l * c;
  if (v >= 20) return 'critical';
  if (v >= 12) return 'high';
  if (v >= 6) return 'medium';
  return 'low';
}

const LEVEL_CLASS: Record<RiskLevel, string> = {
  low: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900',
  medium: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900',
  high: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-900',
  critical: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900',
};

function plusMonths(months: number): string {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

/**
 * Score a supplier on every risk category (likelihood × consequence, 1–5 each).
 * Creates the assessment, then one score per category; the backend derives the
 * overall level from them.
 */
export default function RiskAssessmentDialog({ open, onClose, supplierId, customerId, assessorId, categories, onSaved }: RiskAssessmentDialogProps) {
  const [cells, setCells] = useState<Record<number, CellState>>({});
  const [nextReview, setNextReview] = useState(plusMonths(12));
  const [summary, setSummary] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial: Record<number, CellState> = {};
    categories.forEach((c) => { initial[c.id] = { likelihood: 1, consequence: 1, notes: '' }; });
    setCells(initial);
    setNextReview(plusMonths(12));
    setSummary('');
    setRecommendations('');
  }, [open, categories]);

  const setCell = (id: number, patch: Partial<CellState>) =>
    setCells((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const submit = async () => {
    if (categories.length === 0) { toast.error('No risk categories are set up.'); return; }
    setSaving(true);
    let createdId: number | null = null;
    try {
      const created = await riskAPI.createAssessment({
        supplier: supplierId, customer: customerId, status: 'completed',
        assessment_date: new Date().toISOString().slice(0, 10),
        next_review_date: nextReview || null, summary, recommendations, overall_risk_level: 'low',
        assessed_by: assessorId,
      });
      createdId = created.data.id;
      for (const category of categories) {
        const cell = cells[category.id];
        // The score's description is required server-side; fall back to the scoring itself.
        const description = cell.notes.trim()
          || `${category.name}: likelihood ${LIKELIHOOD[cell.likelihood - 1]}, consequence ${CONSEQUENCE[cell.consequence - 1]}`;
        await riskAPI.createScore({
          assessment: created.data.id, category: category.id,
          likelihood: cell.likelihood, consequence: cell.consequence, risk_description: description,
        });
      }
      const fresh = await riskAPI.assessment(created.data.id);
      toast.success(`Risk assessment saved — ${fresh.data.overall_risk_level} risk`);
      onSaved(fresh.data);
      onClose();
    } catch (err: unknown) {
      console.error('Risk assessment save failed:', err);
      toast.error(getErrorMessage(err, 'Could not save the risk assessment'));
      // A half-scored assessment would show as "low" risk; take it back out.
      if (createdId !== null) {
        try { await riskAPI.deleteAssessment(createdId); } catch (cleanupErr: unknown) { console.error('Cleanup failed:', cleanupErr); }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="risk-dialog">
        <DialogHeader><DialogTitle>New risk assessment</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Score each category: how likely is the risk, and how bad if it happens. 1 is lowest, 5 highest.</p>
        <div className="space-y-3">
          {categories.map((category) => {
            const cell = cells[category.id] ?? { likelihood: 1, consequence: 1, notes: '' };
            const level = cellLevel(cell.likelihood, cell.consequence);
            return (
              <div key={category.id} className="rounded-md border p-3" data-testid={`risk-category-${category.id}`}>
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium text-foreground">{category.name}</p>
                    <p className="text-xs text-muted-foreground">{category.description}</p>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${LEVEL_CLASS[level]}`}>
                    {level} · {cell.likelihood * cell.consequence}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Likelihood</Label>
                    <Select value={String(cell.likelihood)} onValueChange={(v) => setCell(category.id, { likelihood: Number(v) })}>
                      <SelectTrigger data-testid={`risk-likelihood-${category.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{LIKELIHOOD.map((label, i) => <SelectItem key={i} value={String(i + 1)}>{i + 1} · {label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Consequence</Label>
                    <Select value={String(cell.consequence)} onValueChange={(v) => setCell(category.id, { consequence: Number(v) })}>
                      <SelectTrigger data-testid={`risk-consequence-${category.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{CONSEQUENCE.map((label, i) => <SelectItem key={i} value={String(i + 1)}>{i + 1} · {label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Input value={cell.notes} onChange={(e) => setCell(category.id, { notes: e.target.value })} placeholder="Why this score" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="risk-review">Next review</Label>
            <Input id="risk-review" type="date" value={nextReview} onChange={(e) => setNextReview(e.target.value)} data-testid="risk-next-review" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="risk-summary">Summary</Label>
            <Textarea id="risk-summary" rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="risk-recs">Recommendations / mitigation</Label>
            <Textarea id="risk-recs" rows={2} value={recommendations} onChange={(e) => setRecommendations(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || categories.length === 0} data-testid="risk-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save assessment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
